import type { UserAccount } from '../../../shared/api/users.api'
import { CityDropdown } from './CityDropdown'
import { AssistantManagersDropdown, type AssistantManagerOption } from './AssistantManagersDropdown'

export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Администратор',
  GENERAL_DIRECTOR: 'Генеральный директор',
  MANAGER: 'Менеджер по продажам',
  PROCUREMENT: 'Менеджер по закупкам',
  ROP: 'РОП · Руководитель отдела продаж',
  ROS: 'РОЗ · Руководитель отдела закупок',
  ASSISTANT: 'Помощник',
  // Роли этапа 3 (23.09.2026): права выдаются по кускам этапа.
  ACCOUNTANT: 'Бухгалтер',
  LOGIST: 'Логист',
  WAREHOUSE_HEAD: 'Руководитель склада',
  WAREHOUSE_QC: 'Приёмка и ОТК',
  WAREHOUSE_KEEPER: 'Кладовщик',
}

const ROLE_BADGE: Record<string, string> = {
  ADMIN: 'bg-purple-100 text-purple-700',
  GENERAL_DIRECTOR: 'bg-rose-100 text-rose-700',
  MANAGER: 'bg-blue-100 text-blue-700',
  PROCUREMENT: 'bg-green-100 text-green-700',
  ROP: 'bg-indigo-100 text-indigo-700',
  ROS: 'bg-emerald-100 text-emerald-700',
  ASSISTANT: 'bg-amber-100 text-amber-700',
}

/** Роли, для которых показываем выбор города.
 * MANAGER добавлен 27.04.2026: после смены модели city-фильтра менеджер
 * получил «свой город», по которому фильтруются закупщики/РОП/РОЗ. */
const ROLES_WITH_CITY_FILTER = new Set(['MANAGER', 'PROCUREMENT', 'ROP', 'ROS'])

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[role] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {ROLE_LABELS[role] ?? role}
    </span>
  )
}

export function UserRow({
  account,
  currentUserId,
  allCities,
  managerOptions,
  readOnly = false,
  onEdit,
  onDelete,
  onToggleBlock,
  onSaveCities,
  onSaveAssistantManagers,
}: {
  account: UserAccount
  currentUserId: string
  allCities: string[]
  /** Активные MANAGER/ROP — кандидаты на прикрепление к помощнику. */
  managerOptions: AssistantManagerOption[]
  /** true для Генерального директора — только просмотр, без кнопок действий. */
  readOnly?: boolean
  onEdit: (a: UserAccount) => void
  onDelete: (a: UserAccount) => void
  onToggleBlock: (a: UserAccount) => void
  onSaveCities: (id: string, cities: string[], excludeMode: boolean) => void
  onSaveAssistantManagers: (id: string, managerIds: string[]) => void
}) {
  const isSelf = account.id === currentUserId
  const isAdmin = account.role === 'ADMIN'
  const canEdit = !readOnly && (!isAdmin || isSelf)
  const canDelete = !readOnly && !isAdmin && !isSelf
  const canBlock = !readOnly && !isAdmin && !isSelf

  return (
    <tr className={account.isBlocked ? 'opacity-60' : ''}>
      <td className="px-4 py-3">
        <div className="font-medium text-gray-800 text-sm">{account.name || '—'}</div>
        {isSelf && <div className="text-xs text-brand-600">это вы</div>}
      </td>
      <td className="px-4 py-3 text-gray-600 text-sm">{account.email}</td>
      <td className="px-4 py-3 text-gray-400 text-sm tracking-widest">••••••••</td>
      <td className="px-4 py-3">
        <RoleBadge role={account.role} />
      </td>
      <td className="px-4 py-3">
        {account.role === 'ASSISTANT' ? (
          // Для помощника в этой колонке — прикрепление к менеджерам
          // (городского фильтра у роли нет: видимость идёт от менеджеров).
          readOnly ? (
            <span className="text-xs text-gray-500">
              {(account.assistantManagerIds ?? []).length > 0
                ? `Менеджеров: ${(account.assistantManagerIds ?? []).length}`
                : 'Не прикреплён'}
            </span>
          ) : (
            <AssistantManagersDropdown
              selected={account.assistantManagerIds ?? []}
              options={managerOptions}
              onSave={(ids) => onSaveAssistantManagers(account.id, ids)}
            />
          )
        ) : ROLES_WITH_CITY_FILTER.has(account.role) ? (
          readOnly ? (
            <span className="text-xs text-gray-500">
              {(account.cities ?? []).length === 0 ? 'Все' : (account.cities ?? []).join(', ')}
            </span>
          ) : (
            <CityDropdown
              selected={account.cities ?? []}
              excludeMode={account.citiesExcludeMode ?? false}
              allCities={allCities}
              onSave={(cities, excludeMode) => onSaveCities(account.id, cities, excludeMode)}
            />
          )
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {account.isBlocked ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
            Заблокирован
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Активен
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => onEdit(account)}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors"
            >
              Изменить
            </button>
          )}
          {canBlock && (
            <button
              onClick={() => onToggleBlock(account)}
              className={`text-xs font-medium transition-colors ${
                account.isBlocked
                  ? 'text-green-600 hover:text-green-700'
                  : 'text-amber-600 hover:text-amber-700'
              }`}
            >
              {account.isBlocked ? 'Разблокировать' : 'Заблокировать'}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(account)}
              className="text-xs text-red-400 hover:text-red-600 transition-colors"
            >
              Удалить
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}
